// The server, as the client sees it. One place that knows the endpoints exist,
// so the store reads as sync logic rather than as fetch plumbing.
//
// **Every workspace call is `/api/w/<slug>/...`.** The slug is read from the URL
// once, at boot (`setWorkspace` in main.tsx), and prefixed here, so no component
// ever builds a workspace path by hand.

import type { Brand } from './brand'
import type { CrumEvent } from './events'
import type { Config } from './model'

let ws = ''

/** Called once at boot with the slug from `/w/<slug>` or `/r/<slug>/<token>`. */
export function setWorkspace(slug: string): void {
  ws = slug
}

export function workspaceSlug(): string {
  return ws
}

function w(path: string): string {
  return `/api/w/${encodeURIComponent(ws)}${path}`
}

/** Where a blob is served from. Public reads, per workspace. */
export function blobUrl(key: string): string {
  return `/blob/${encodeURIComponent(ws)}/${key}`
}

/** The page a client opens. */
export function clientPageUrl(token: string): string {
  return `/r/${encodeURIComponent(ws)}/${encodeURIComponent(token)}`
}

export interface Bootstrap {
  me: { sub: string; email: string; name: string; admin: boolean }
  workspace: { slug: string; name: string }
  config: Config
  brand: Brand
  seq: number
  /** The public demo: everything is real except that it resets nightly. */
  demo: boolean
}

/** What a signed-out visitor to a workspace may know: enough to draw its door. */
export interface PublicInfo {
  brand: Brand
  workspace: { slug: string; name: string }
  demo: boolean
  /** The demo's sign-in-with-a-name. Off everywhere else. */
  guestAccess: boolean
}

export interface Seat {
  slug: string
  name: string
  kind: 'internal' | 'client'
  orgId: string
  personId: string
  access: 'default' | 'admin' | 'none'
}

/** Who am I, and where does every identity on this device have a seat. */
export interface Me {
  me: { id: string; email: string; name: string; anonymous: boolean }
  superadmin: boolean
  seats: Seat[]
  /** The other identities signed in on this device, and what each can reach.
   *  `token` makes one of them active — see `switchIdentity`. */
  identities: Array<{
    email: string
    name: string
    token: string
    superadmin: boolean
    seats: Seat[]
  }>
}

/** A proposal about **one** change to the log. Never part of the log itself. */
export interface Suggestion {
  id: string
  batch: string
  note: string
  by: string
  at: string
  type: string
  subject: string
  data: string | null
  status: 'pending' | 'approved' | 'denied'
  decidedBy: string | null
  decidedAt: string | null
  reason: string
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin', ...init })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null
    throw new ApiError(res.status, body?.error ?? `${res.status} from ${path}`)
  }
  return (await res.json()) as T
}

function json(body: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }
}

export const api = {
  /** Readable signed-out: it is what brands the sign-in screen. */
  info: () => request<PublicInfo>(w('/brand')),

  /** Throws ApiError(401) when signed out, which is how the app knows to show
   *  the sign-in screen rather than the product. */
  bootstrap: () => request<Bootstrap>(w('/bootstrap')),

  /** Who am I, across workspaces. 401 when signed out. */
  me: () => request<Me>('/api/me'),

  /** Make another identity on this device the active one. */
  switchIdentity: (sessionToken: string) =>
    fetch('/api/auth/multi-session/set-active', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken }),
    }),

  events: (since: number) =>
    request<{ events: Array<CrumEvent & { seq: number }>; seq: number; more: boolean }>(
      w(`/events?since=${since}`),
    ),

  append: (events: CrumEvent[]) =>
    request<{ accepted: number; seq: number }>(w('/events'), json({ events })),

  saveConfig: (config: Config) =>
    request<{ ok: true }>(w('/config'), { ...json(config), method: 'PUT' }),

  saveBrand: async (brand: Brand) =>
    (await request<{ brand: Brand }>(w('/brand'), { ...json(brand), method: 'PUT' })).brand,

  /** Ask for a way in when you know your address and not your workspace: one letter,
   *  and it lands on `/home`, which lists everywhere the address can reach. Answers
   *  ok whatever was typed — see the route. */
  signIn: (email: string) => request<{ ok: true }>('/api/signin', json({ email })),

  /** Ask for a way in: one email carrying a link and a code. Always answers ok,
   *  whether or not the address is on the roster — that is not something this
   *  endpoint will say. */
  requestCode: (email: string) => request<{ ok: true }>(w('/auth/link'), json({ email })),

  /** Redeem the code. Goes straight to the library, which is the one sign-in
   *  endpoint safe to expose: it needs the code. Signs in the window that asks,
   *  which is the whole point on an installed app. */
  submitCode: (email: string, otp: string) =>
    request<unknown>('/api/auth/sign-in/email-otp', json({ email, otp })),

  /** The demo's sign-in. A name, no credential; refused where guest access is off. */
  guestLogin: (name: string) => request<{ me: Bootstrap['me'] }>(w('/auth/guest'), json({ name })),

  /**
   * End the session on this device.
   *
   * The content type is not decoration. Better Auth answers a POST without
   * `application/json` with a 415, and this was sending neither a header nor a
   * body: the call came back, the screen redirected to `/`, and the cookies were
   * all still there. `/` then saw a session and sent you straight back in, so
   * signing out of the demo did nothing and looked like it had worked.
   *
   * `crum_ws` goes too. It only remembers which workspace to open, but leaving it
   * behind sends the next visit to `/` back to the door you just left.
   */
  logout: async () => {
    await fetch('/api/auth/sign-out', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
    document.cookie = 'crum_ws=; Path=/; Max-Age=0; SameSite=Lax'
  },

  /** JSONL in. Idempotent by event id, so re-running it adds nothing. */
  importJsonl: (text: string) =>
    request<{ read: number; added: number; seq: number }>(w('/import'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-ndjson' },
      body: text,
    }),

  /** Mint (or fetch) the client's link for an org. Minting twice returns the same
   *  token: two live links to one page is two things to revoke. */
  share: (org: string) => request<{ token: string }>(w('/share'), json({ org })),

  shares: () =>
    request<{
      shares: Record<string, { org: string; createdAt: string; expiresAt: string | null }>
    }>(w('/shares')),

  revokeShare: (token: string) =>
    request<{ ok: true }>(w(`/share/${encodeURIComponent(token)}`), { method: 'DELETE' }),

  suggestions: (status: 'pending' | 'all' = 'pending') =>
    request<{ suggestions: Suggestion[] }>(w(`/suggestions?status=${status}`)),

  approveSuggestion: (id: string) =>
    request<{ ok: true }>(w(`/suggestions/${encodeURIComponent(id)}/approve`), {
      method: 'POST',
    }),

  approveBatch: (batch: string) =>
    request<{ ok: true; applied: number }>(
      w(`/suggestions/batch/${encodeURIComponent(batch)}/approve`),
      { method: 'POST' },
    ),

  denySuggestion: (id: string, reason: string) =>
    request<{ ok: true }>(w(`/suggestions/${encodeURIComponent(id)}/deny`), json({ reason })),

  agentSeats: () =>
    request<{
      seats: Array<{ id: string; name: string; createdAt: string; revokedAt: string | null }>
    }>(w('/agent-seats')),

  /** The key comes back once and is never shown again. */
  mintAgentSeat: (name: string) =>
    request<{ token: string; name: string }>(w('/agent-seats'), json({ name })),

  revokeAgentSeat: (id: string) =>
    request<{ ok: true }>(w(`/agent-seats/${encodeURIComponent(id)}`), { method: 'DELETE' }),

  /** Upload a blob and get back the key to reference it by. */
  putBlob: async (key: string, file: Blob): Promise<string> => {
    const res = await fetch(w(`/blob/${key}`), {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    })
    if (!res.ok) throw new ApiError(res.status, `upload failed (${res.status})`)
    return ((await res.json()) as { key: string }).key
  },

  seedDemo: () => request<{ added: number; seq: number }>(w('/demo/seed'), { method: 'POST' }),
}

// ── the superadmin surface ───────────────────────────────────────────────────

export interface AdminWorkspace {
  slug: string
  name: string
  status: 'active' | 'suspended'
  allowList: string[]
  guestAccess: boolean
  isDemo: boolean
  createdBy: string
  createdAt: string
  members: number
}

export interface AdminMember {
  slug: string
  email: string
  kind: 'internal' | 'client'
  orgId: string
  personId: string
  name: string
  access: 'default' | 'admin' | 'none'
}

export const adminApi = {
  /** A superadmin asking for a link. Always answers ok. */
  requestLink: (email: string) => request<{ ok: true }>('/api/admin-link', json({ email })),

  workspaces: () => request<{ workspaces: AdminWorkspace[] }>('/api/admin/workspaces'),

  createWorkspace: (body: {
    slug: string
    name: string
    allowList: string
    first: { name: string; email: string }
    sendLink?: boolean
  }) => request<{ ok: true; slug: string }>('/api/admin/workspaces', json(body)),

  patchWorkspace: (
    slug: string,
    patch: Partial<{
      name: string
      allowList: string
      guestAccess: boolean
      isDemo: boolean
      status: 'active' | 'suspended'
    }>,
  ) =>
    request<{ ok: true; workspace: AdminWorkspace }>(
      `/api/admin/workspaces/${encodeURIComponent(slug)}`,
      { ...json(patch), method: 'PATCH' },
    ),

  members: (slug: string) =>
    request<{ members: AdminMember[] }>(
      `/api/admin/workspaces/${encodeURIComponent(slug)}/members`,
    ),

  invite: (slug: string, body: { name: string; email: string; access?: 'default' | 'admin' }) =>
    request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(slug)}/invite`, json(body)),

  /** Empty a workspace's records. The team is re-seeded, so the door stays open. */
  reset: (slug: string, confirm: string) =>
    request<{ ok: true; restored: number }>(
      `/api/admin/workspaces/${encodeURIComponent(slug)}/reset`,
      json({ confirm }),
    ),

  rebuild: (slug: string) =>
    request<{ ok: true; members: number }>(
      `/api/admin/workspaces/${encodeURIComponent(slug)}/rebuild`,
      { method: 'POST' },
    ),

  superadmins: () =>
    request<{ superadmins: Array<{ email: string; source: string }> }>('/api/admin/superadmins'),

  addSuperadmin: (email: string) =>
    request<{ ok: true }>('/api/admin/superadmins', json({ email })),

  removeSuperadmin: (email: string) =>
    request<{ ok: true }>(`/api/admin/superadmins/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    }),
}
